import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { QRCodeCanvas } from "qrcode.react";
import { toast } from "sonner";
import { CalendarDays, Download, Flower2, Loader2, LogOut, MessageCircle, RefreshCw, Trash2, UserPlus, Users } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import Appointments from "@/pages/admin/Appointments";
import Patients from "@/pages/admin/Patients";

export default function Dashboard() {
  const { user, setUser } = useAuth();
  const navigate = useNavigate();
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const qrWrapRef = useRef(null);
  const siteUrl = window.location.origin;

  const fetchLeads = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get("/leads");
      setLeads(res.data);
    } catch {
      toast.error("No se pudieron cargar los contactos");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchLeads();
  }, [fetchLeads]);

  const exportCsv = async () => {
    try {
      const res = await api.get("/leads/export", { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = "contactos-web.csv";
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Lista exportada en CSV");
    } catch {
      toast.error("No se pudo exportar");
    }
  };

  const deleteLead = async (id) => {
    try {
      await api.delete(`/leads/${id}`);
      setLeads((prev) => prev.filter((l) => l.id !== id));
      toast.success("Contacto eliminado");
    } catch {
      toast.error("No se pudo eliminar");
    }
  };

  const downloadQR = () => {
    const canvas = qrWrapRef.current?.querySelector("canvas");
    if (!canvas) return;
    const a = document.createElement("a");
    a.href = canvas.toDataURL("image/png");
    a.download = "qr-elba-rojas.png";
    a.click();
    toast.success("QR descargado, listo para imprimir");
  };

  const logout = async () => {
    try {
      await api.post("/auth/logout");
    } finally {
      setUser(false);
      navigate("/admin/login");
    }
  };

  const today = new Date().toDateString();
  const todayCount = leads.filter((l) => new Date(l.created_at).toDateString() === today).length;
  const referrals = leads.filter((l) => l.referido).length;

  const waLink = (phone, nombre) => {
    const digits = String(phone).replace(/\D/g, "");
    const num = digits.length === 8 ? `507${digits}` : digits;
    const text = `Hola ${String(nombre).split(" ")[0]}, soy Elba Rojas, psicóloga. Te contacto porque dejaste tus datos en mi página web. ¿Te gustaría agendar una consulta?`;
    return `https://wa.me/${num}?text=${encodeURIComponent(text)}`;
  };

  return (
    <div className="min-h-screen bg-sand text-ink" data-testid="admin-dashboard">
      <header className="bg-white border-b border-line">
        <div className="max-w-6xl mx-auto px-6 h-16 flex items-center justify-between">
          <p className="flex items-center gap-2 font-serif text-lg">
            <Flower2 className="h-5 w-5 text-sage" /> Elba Rojas <span className="text-xs uppercase tracking-[0.2em] text-stonemute font-sans font-bold">Panel</span>
          </p>
          <Button variant="outline" onClick={logout} data-testid="logout-button"
            className="rounded-full border-line text-sm hover:bg-sage hover:text-white hover:border-sage">
            <LogOut className="h-4 w-4 mr-2" /> Salir
          </Button>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 py-10 space-y-8">
        <Tabs defaultValue="contactos" data-testid="admin-tabs">
          <TabsList className="bg-white border border-line">
            <TabsTrigger value="contactos" data-testid="tab-leads">Contactos</TabsTrigger>
            <TabsTrigger value="citas" data-testid="tab-appointments">Citas y finanzas</TabsTrigger>
            <TabsTrigger value="pacientes" data-testid="tab-patients">Pacientes</TabsTrigger>
          </TabsList>
          <TabsContent value="contactos" className="space-y-8 mt-6">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          {[
            { icon: Users, label: "Contactos totales", value: leads.length, testid: "stat-total-leads" },
            { icon: CalendarDays, label: "Registros de hoy", value: todayCount, testid: "stat-today-leads" },
            { icon: UserPlus, label: "Con referido", value: referrals, testid: "stat-referrals" },
          ].map(({ icon: Icon, label, value, testid }) => (
            <div key={testid} className="bg-white border border-line rounded-lg p-5 flex items-center gap-4" data-testid={testid}>
              <span className="grid place-items-center h-11 w-11 rounded-full bg-sand border border-line">
                <Icon className="h-5 w-5 text-sage" />
              </span>
              <div>
                <p className="font-serif text-3xl leading-none">{value}</p>
                <p className="text-xs uppercase tracking-[0.15em] text-stonemute mt-1">{label}</p>
              </div>
            </div>
          ))}
        </div>

        <div className="grid lg:grid-cols-[320px_1fr] gap-6 items-start">
          <div className="bg-white border border-line rounded-lg p-6 flex flex-col items-center text-center gap-4">
            <p className="text-xs uppercase tracking-[0.2em] font-bold text-sage">Código QR de tu página</p>
            <div ref={qrWrapRef} data-testid="qr-code-image" className="p-3 border border-line rounded-lg bg-white">
              <QRCodeCanvas value={siteUrl} size={1024} style={{ width: 200, height: 200 }} includeMargin />
            </div>
            <p className="text-xs text-stonemute break-all" data-testid="qr-site-url">{siteUrl}</p>
            <Button onClick={downloadQR} data-testid="qr-download-button"
              className="w-full rounded-full bg-sage text-white text-sm font-bold hover:bg-sage-dark">
              <Download className="h-4 w-4 mr-2" /> Descargar QR para imprimir
            </Button>
            <p className="text-xs text-stonemute">Imprímelo para tu consultorio o compártelo en redes. Quien lo escanee llegará a tu página de registro.</p>
          </div>

          <div className="bg-white border border-line rounded-lg overflow-hidden">
            <div className="flex items-center justify-between px-5 py-4 border-b border-line">
              <p className="font-serif text-lg">Contactos captados</p>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" onClick={fetchLeads} data-testid="refresh-leads-button"
                  className="rounded-full border-line hover:bg-sand hover:text-ink">
                  <RefreshCw className="h-4 w-4" />
                </Button>
                <Button size="sm" onClick={exportCsv} data-testid="export-csv-button"
                  className="rounded-full bg-sage text-white hover:bg-sage-dark">
                  <Download className="h-4 w-4 mr-2" /> Exportar CSV
                </Button>
              </div>
            </div>
            {loading ? (
              <div className="p-10 grid place-items-center"><Loader2 className="h-6 w-6 animate-spin text-sage" /></div>
            ) : leads.length === 0 ? (
              <p className="p-10 text-center text-sm text-stonemute" data-testid="empty-leads-message">
                Aún no hay contactos. Comparte tu página o el QR para empezar.
              </p>
            ) : (
              <div className="overflow-x-auto">
                <Table data-testid="leads-table">
                  <TableHeader>
                    <TableRow className="border-line">
                      <TableHead>Fecha</TableHead>
                      <TableHead>Nombre</TableHead>
                      <TableHead>Teléfono</TableHead>
                      <TableHead>Edad</TableHead>
                      <TableHead>Motivo</TableHead>
                      <TableHead>Referido</TableHead>
                      <TableHead></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {leads.map((lead) => (
                      <TableRow key={lead.id} className="border-line" data-testid={`lead-row-${lead.id}`}>
                        <TableCell className="whitespace-nowrap text-stonemute text-xs">
                          {new Date(lead.created_at).toLocaleString("es", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" })}
                        </TableCell>
                        <TableCell className="font-medium whitespace-nowrap">{lead.nombre}</TableCell>
                        <TableCell className="whitespace-nowrap">{lead.telefono}</TableCell>
                        <TableCell>{lead.edad}</TableCell>
                        <TableCell className="max-w-[180px] truncate">{lead.motivo}</TableCell>
                        <TableCell className="max-w-[160px] truncate text-stonemute">{lead.referido || "—"}</TableCell>
                        <TableCell>
                          <div className="flex items-center gap-1">
                            <a href={waLink(lead.telefono, lead.nombre)} target="_blank" rel="noopener noreferrer"
                              data-testid={`whatsapp-lead-button-${lead.id}`} title="Escribir por WhatsApp"
                              className="inline-flex items-center justify-center h-8 w-8 rounded-md text-[#1eb85a] transition-colors hover:bg-green-50">
                              <MessageCircle className="h-4 w-4" />
                            </a>
                            <Button variant="ghost" size="sm" onClick={() => deleteLead(lead.id)}
                              data-testid={`delete-lead-button-${lead.id}`}
                              className="text-stonemute hover:text-red-700 hover:bg-red-50">
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </div>
        </div>
          </TabsContent>
          <TabsContent value="citas" className="mt-6">
            <Appointments />
          </TabsContent>
          <TabsContent value="pacientes" className="mt-6">
            <Patients />
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
}
